/**
 * Option A — public demo route rendered at /tour (full path: /tracker/tour).
 * Does NOT require authentication. Injects mock data, disables polling, and
 * auto-starts the guided tour over the full Tracker layout.
 *
 * Two-phase initialisation:
 *   Phase 1 (first useEffect): sets isTourDemo + fake auth + axios mock into the
 *     store synchronously, then flips `ready` to true so Tracker mounts.
 *     This ensures useAuth() inside Tracker sees a valid token on its first render.
 *   Phase 2 (second useEffect, gated on `ready`): injects mock flight data and
 *     starts the Shepherd tour.
 *
 * `body.tour-active` is added when the tour starts and removed on all exit paths
 * (complete, cancel, quit, unmount) so the CSS pointer-events rule in tour.css
 * correctly disables non-Shepherd interactive elements during the tour.
 *
 * teardownTourMockHttp() is called ONLY in Phase 1's cleanup, which runs AFTER all
 * children (Tracker, TetrisPanel) have unmounted — preserving the mock interceptor
 * until TetrisPanel's onUnmount score-POST has completed.
 *
 * On tour COMPLETE: mock data is intentionally kept alive so the user can freely
 * explore the interface. cleanup() is only called on cancel and quit.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import Shepherd from 'shepherd.js';
import { offset } from '@floating-ui/dom';
import type { AuthUser } from '../types/api';
import { useAppStore } from '../store/appStore';
import { useTourMock } from './useTourMock';
import { createTourSteps, closeTourModals } from './tourSteps';
import { setupTourMockHttp, teardownTourMockHttp } from './tourAxiosMock';
import Tracker from '../pages/Tracker';
import TourDemoBanner, { BANNER_HEIGHT_EXPANDED } from './TourDemoBanner';

/** Global Shepherd options. floatingUIOptions adds a 14px gap between bubble and target. */
const TOUR_OPTIONS: ConstructorParameters<typeof Shepherd.Tour>[0] = {
  useModalOverlay: true,
  defaultStepOptions: {
    cancelIcon: { enabled: true },
    scrollTo: { behavior: 'smooth', block: 'center' },
    classes: 'omen-tour-step',
    arrow: true,
    floatingUIOptions: {
      middleware: [offset(14)],
    },
  },
};

/**
 * Builds a structurally valid JWT with a base64url-encoded payload that
 * jwtDecode() can parse. The signature is a placeholder — never validated
 * client-side. The exp is set to a far-future timestamp so the expiry check
 * in useAuth() always passes.
 */
function buildFakeTourJwt(): string {
  const b64url = (obj: unknown): string => {
    const jsonStr = JSON.stringify(obj);
    const bytes = new TextEncoder().encode(jsonStr);
    const binary = Array.from(bytes, (byte) => String.fromCharCode(byte)).join('');
    return btoa(binary)
      .replace(/=/g, '')
      .replace(/\+/g, '-')
      .replace(/\//g, '_');
  };

  const header = b64url({ alg: 'HS256', typ: 'JWT' });
  const payload = b64url({
    user_id: 0,
    name: 'Demo',
    email: 'demo@omen.local',
    role: 'user',
    group_id: 1,
    group_name: 'Demo Group',
    scope: 'tracker',
    exp: 9_999_999_999,
  } satisfies AuthUser & { exp: number });

  return `${header}.${payload}.tour_demo_signature`;
}

const FAKE_TOUR_JWT = buildFakeTourJwt();

const FAKE_AUTH_USER: AuthUser = {
  user_id: 0,
  name: 'Demo',
  email: 'demo@omen.local',
  role: 'user',
  group_id: 1,
  group_name: 'Demo Group',
  scope: 'tracker',
};

/**
 * Public demo route — mounts the full Tracker in demo mode and auto-starts the tour.
 * No auth guard. Polling is disabled via the isTourDemo flag in the store.
 */
export default function TourDemoRoute() {
  const [ready, setReady] = useState(false);
  const [bannerHeight, setBannerHeight] = useState(BANNER_HEIGHT_EXPANDED);

  const { inject, cleanup } = useTourMock();
  const tourRef = useRef<InstanceType<typeof Shepherd.Tour> | null>(null);

  const handleHeightChange = useCallback((h: number) => setBannerHeight(h), []);

  // ── Phase 1 — store setup ─────────────────────────────────────────────────
  useEffect(() => {
    const s = useAppStore.getState();
    s.setTourDemo(true);
    s.setAuth(FAKE_TOUR_JWT, FAKE_AUTH_USER);
    setupTourMockHttp();
    setReady(true);

    return () => {
      document.body.classList.remove('tour-active');
      closeTourModals();
      cleanup();
      useAppStore.getState().setTourDemo(false);
      teardownTourMockHttp();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Phase 2 — data injection + tour launch ────────────────────────────────
  useEffect(() => {
    if (!ready) return;

    inject();

    const timer = setTimeout(() => {
      const tour = new Shepherd.Tour(TOUR_OPTIONS);
      const steps = createTourSteps(inject, cleanup);
      tour.addSteps(steps);

      document.body.classList.add('tour-active');

      // On cancel: clean up mock data, remove tour-active, close open modals.
      tour.on('cancel', () => {
        document.body.classList.remove('tour-active');
        closeTourModals();
        tourRef.current = null;
      });
      // On complete: mock data stays alive for free exploration; remove tour-active.
      tour.on('complete', () => {
        document.body.classList.remove('tour-active');
        closeTourModals();
        tourRef.current = null;
      });

      tourRef.current = tour;
      void tour.start();
    }, 400);

    return () => {
      clearTimeout(timer);
      tourRef.current?.cancel();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready]);

  // ── Quit handler ──────────────────────────────────────────────────────────
  // teardownTourMockHttp intentionally omitted: Phase 1 cleanup handles it
  // after Tracker (and TetrisPanel) unmount.
  const handleQuit = () => {
    tourRef.current?.cancel();
    document.body.classList.remove('tour-active');
    closeTourModals();
    cleanup();
    //useAppStore.getState().setTourDemo(false);
    useAppStore.getState().logout();
  };

  if (!ready) {
    return <TourDemoBanner onQuit={handleQuit} onHeightChange={handleHeightChange} />;
  }

  return (
    <>
      <TourDemoBanner onQuit={handleQuit} onHeightChange={handleHeightChange} />
      <div style={{ paddingTop: `${bannerHeight}px`, height: '100vh', boxSizing: 'border-box' }}>
        <Tracker />
      </div>
    </>
  );
}
